import React, { useEffect, useMemo, useState, useCallback } from "react";
import { format } from "date-fns";

import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { useHasModule } from "@/hooks/usePermissions";
import { getStoredUser } from "@/lib/auth";
import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import InCardFilterPanel from "@/components/leads/InCardFilterPanel";

import { 
  ChevronDown, 
  Plus, 
  Trash2,
  Megaphone,
  Users,
  UserCheck,
  CalendarCheck,
  MoreHorizontal,
  Pause,
  Play,
  Archive,
  Edit,
  Link,
  ListFilter,
  Settings2,
  ArrowUpDown,
  ArrowDownAZ,
  ArrowUpAZ,
} from "lucide-react";

// ====== CONSTANTS ======

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "archived", label: "Archived" },
];

// MA-UI-127: Message Categories — DEPRECATED (MA-ARCH-172: owned by Profit Path now)
// Retained as reference only; not used in Campaign UI surfaces.

const SOURCE_TYPE_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "organic", label: "Organic" },
  { value: "referral", label: "Referral" },
  { value: "direct", label: "Direct" },
  { value: "email", label: "Email" },
  { value: "social", label: "Social" },
  { value: "other", label: "Other" },
];

const SOURCE_PLATFORM_OPTIONS = [
  { value: "meta", label: "Meta (Facebook/Instagram)" },
  { value: "google", label: "Google Ads" },
  { value: "tiktok", label: "TikTok" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "Twitter/X" },
  { value: "youtube", label: "YouTube" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "organic", label: "Organic Search" },
  { value: "direct", label: "Direct Traffic" },
  { value: "referral", label: "Referral" },
  { value: "other", label: "Other" },
];

const DEFAULT_LIMIT = 25;

const DEFAULT_TABLE_COLUMNS = [
  "name",
  "source_platform",
  "status",
  "lead_count",
  "qualified_count",
  "booked_count",
];

const CAMPAIGN_FIELD_OPTIONS = [
  { key: "name", label: "Name" },
  { key: "source_platform", label: "Platform" },
  { key: "source_type", label: "Source Type" },
  { key: "status", label: "Status" },
  { key: "utm_source", label: "UTM Source" },
  { key: "utm_medium", label: "UTM Medium" },
  { key: "utm_campaign", label: "UTM Campaign" },
  { key: "lead_count", label: "Leads" },
  { key: "qualified_count", label: "Qualified" },
  { key: "booked_count", label: "Booked" },
  { key: "created_at", label: "Created" },
];

// Filter field definitions for the filter panel
const FILTER_FIELD_DEFS = [
  { key: "status", label: "Status", type: "select" },
  { key: "source_platform", label: "Platform", type: "select" },
  { key: "source_type", label: "Source Type", type: "select" },
];

// ====== HELPER FUNCTIONS ======

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function generateTrackingParams(campaign) {
  const params = new URLSearchParams();
  params.set("campaign_id", campaign.id);
  if (campaign.utm_source) params.set("utm_source", campaign.utm_source);
  if (campaign.utm_medium) params.set("utm_medium", campaign.utm_medium);
  if (campaign.utm_campaign) params.set("utm_campaign", campaign.utm_campaign);
  if (campaign.utm_content) params.set("utm_content", campaign.utm_content);
  if (campaign.utm_term) params.set("utm_term", campaign.utm_term);
  return params.toString();
}

function generateExampleTrackingUrl(campaign) {
  const params = generateTrackingParams(campaign);
  return `https://your-landing-page.com/?${params}`;
}

function prettifyFieldLabel(key) {
  if (!key) return "";
  const overrides = {
    lead_count: "Leads",
    qualified_count: "Qualified",
    booked_count: "Booked",
    source_platform: "Platform",
    source_type: "Source Type",
    utm_source: "UTM Source",
    utm_medium: "UTM Medium",
    utm_campaign: "UTM Campaign",
    created_at: "Created",
  };
  if (overrides[key]) return overrides[key];
  return key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// ====== STATUS BADGE ======

function CampaignStatusBadge({ status }) {
  const styles = {
    draft: "bg-slate-500/20 text-slate-300 border-slate-400/20",
    active: "bg-green-500/20 text-green-400 border-green-400/20",
    paused: "bg-amber-500/20 text-amber-400 border-amber-400/20",
    archived: "bg-slate-600/20 text-slate-400 border-slate-500/20",
  };
  
  return (
    <Badge variant="outline" className={styles[status] || styles.draft}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// ====== SUMMARY CARD ======

function SummaryCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    indigo: "bg-indigo-500/10 text-indigo-400",
    green: "bg-green-500/10 text-green-400",
    amber: "bg-amber-500/10 text-amber-400",
    cyan: "bg-cyan-500/10 text-cyan-400",
  };
  
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colorClasses[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-semibold text-slate-100">{value}</div>
        <div className="text-xs text-slate-200/60">{label}</div>
      </div>
    </div>
  );
}

// ====== CREATE/EDIT MODAL ======

function CampaignModal({ open, onOpenChange, campaign, onSave }) {
  const isEdit = !!campaign;
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    status: "draft",
    source_type: "paid",
    source_platform: "meta",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    external_campaign_id: "",
    notes: "",
  });

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name || "",
        status: campaign.status || "draft",
        source_type: campaign.source_type || "paid",
        source_platform: campaign.source_platform || "meta",
        utm_source: campaign.utm_source || "",
        utm_medium: campaign.utm_medium || "",
        utm_campaign: campaign.utm_campaign || "",
        utm_content: campaign.utm_content || "",
        utm_term: campaign.utm_term || "",
        external_campaign_id: campaign.external_campaign_id || "",
        notes: campaign.notes || "",
      });
    } else {
      setFormData({
        name: "",
        status: "draft",
        source_type: "paid",
        source_platform: "meta",
        utm_source: "",
        utm_medium: "",
        utm_campaign: "",
        utm_content: "",
        utm_term: "",
        external_campaign_id: "",
        notes: "",
      });
    }
  }, [campaign, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    setLoading(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };
  
  const canSubmit = formData.name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-w-xl border-white/10 text-slate-100">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Traffic Source" : "Create Traffic Source"}</DialogTitle>
          <DialogDescription className="text-slate-200/60">
            {isEdit ? "Update traffic source details and attribution settings." : "Set up a new traffic source to track where leads come from."}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">Campaign Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Meta - Medicare AEP 2025"
                className="bg-white/5 border-white/10"
                required
                data-testid="campaign-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="campaign-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="source_platform">Platform</Label>
              <Select
                value={formData.source_platform}
                onValueChange={(v) => setFormData({ ...formData, source_platform: v })}
              >
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="campaign-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {SOURCE_PLATFORM_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* UTM Parameters */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-slate-200/80">UTM Parameters (for attribution matching)</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                value={formData.utm_source}
                onChange={(e) => setFormData({ ...formData, utm_source: e.target.value })}
                placeholder="utm_source"
                className="bg-white/5 border-white/10"
                data-testid="campaign-utm-source"
              />
              <Input
                value={formData.utm_medium}
                onChange={(e) => setFormData({ ...formData, utm_medium: e.target.value })}
                placeholder="utm_medium"
                className="bg-white/5 border-white/10"
                data-testid="campaign-utm-medium"
              />
              <Input
                value={formData.utm_campaign}
                onChange={(e) => setFormData({ ...formData, utm_campaign: e.target.value })}
                placeholder="utm_campaign"
                className="bg-white/5 border-white/10"
                data-testid="campaign-utm-campaign"
              />
              <Input
                value={formData.utm_content}
                onChange={(e) => setFormData({ ...formData, utm_content: e.target.value })}
                placeholder="utm_content"
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          
          {/* External ID */}
          <div className="space-y-2">
            <Label htmlFor="external_campaign_id">External Campaign ID (optional)</Label>
            <Input
              id="external_campaign_id"
              value={formData.external_campaign_id}
              onChange={(e) => setFormData({ ...formData, external_campaign_id: e.target.value })}
              placeholder="Ad platform campaign ID"
              className="bg-white/5 border-white/10"
            />
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Internal notes about this campaign..."
              className="bg-white/5 border-white/10 min-h-[80px]"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canSubmit} data-testid="campaign-save-btn">
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Create Campaign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ====== DELETE CONFIRM DIALOG ======

function DeleteConfirmDialog({ open, onOpenChange, campaign, onConfirm }) {
  const [loading, setLoading] = useState(false);
  
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 text-slate-100">
        <DialogHeader>
          <DialogTitle>Delete Campaign</DialogTitle>
          <DialogDescription className="text-slate-200/60">
            Are you sure you want to delete "{campaign?.name}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm} disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== COLUMNS DIALOG ======

function CampaignColumnsDialog({ open, onOpenChange, columns, onSave }) {
  const [selected, setSelected] = useState(columns);

  useEffect(() => {
    setSelected(columns);
  }, [columns, open]);

  const toggleColumn = (key) => {
    setSelected(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key) 
        : [...prev, key]
    );
  };

  const handleSave = () => {
    onSave(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 text-slate-100">
        <DialogHeader>
          <DialogTitle>Manage Columns</DialogTitle>
          <DialogDescription className="text-slate-200/60">
            Choose which columns to display in the campaigns table.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 py-4">
          {CAMPAIGN_FIELD_OPTIONS.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-white/5">
              <Checkbox
                checked={selected.includes(key)}
                onCheckedChange={() => toggleColumn(key)}
                className="border-white/20 data-[state=checked]:bg-indigo-500/90"
              />
              <span className="text-sm text-slate-200">{label}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== MAIN COMPONENT ======

export default function CampaignsPage() {
  usePageTitle("Campaigns");
  const { toast } = useToast();
  const user = getStoredUser();
  const { selectedTenantId } = useTenant();

  // MA-PHASE10: Use module-based permissions instead of role checks
  const { hasAccess: canManageCampaigns } = useHasModule("campaigns");

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    total_leads: 0,
    total_qualified: 0,
    total_booked: 0,
  });

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  // Sorting
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // Filter panel state (like Leads)
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("campaigns_filters_open");
      return stored !== null ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("campaigns_filters_open", String(filtersOpen));
    } catch {}
  }, [filtersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  // Columns
  const [tableColumns, setTableColumns] = useState(() => {
    try {
      const stored = localStorage.getItem("campaigns_table_columns");
      return stored ? JSON.parse(stored) : DEFAULT_TABLE_COLUMNS;
    } catch {
      return DEFAULT_TABLE_COLUMNS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("campaigns_table_columns", JSON.stringify(tableColumns));
    } catch {}
  }, [tableColumns]);

  const [columnsOpen, setColumnsOpen] = useState(false);

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, campaign: null });

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Build filter options from data
  const filterOptions = useMemo(() => ({
    status: STATUS_OPTIONS.map(o => ({ value: o.value, label: o.label })),
    source_platform: SOURCE_PLATFORM_OPTIONS.map(o => ({ value: o.value, label: o.label })),
    source_type: SOURCE_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
  }), []);

  // Check if filters are active
  const filtersActive = Object.keys(filtersApplied).some(k => {
    const v = filtersApplied[k];
    if (!v) return false;
    if (Array.isArray(v.value)) return v.value.length > 0;
    return !!v.value;
  });

  // ====== DATA FETCHING ======

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (qDebounced) params.set("q", qDebounced);
      
      // Apply filters from filtersApplied
      if (filtersApplied.status?.value) params.set("status", filtersApplied.status.value);
      if (filtersApplied.source_platform?.value) params.set("source_platform", filtersApplied.source_platform.value);
      if (filtersApplied.source_type?.value) params.set("source_type", filtersApplied.source_type.value);

      const res = await api.get(`/marketing-campaigns?${params.toString()}`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setSelected(new Set());
    } catch (e) {
      // MA-PHASE11A-P0-AUDIT-005: On error, clear items to prevent stale data from previous account
      console.error("Failed to load campaigns:", e);
      setItems([]);  // Clear stale data
      setTotal(0);
      toast({ title: "Failed to load campaigns", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, qDebounced, filtersApplied]);  // MA-PHASE11A: Removed `toast` from deps to prevent duplicate toasts

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get("/marketing-campaigns/summary");
      setSummary(res.data || {});
    } catch {
      // MA-PHASE11A-P0-AUDIT-005: Clear summary on error
      setSummary({});
    }
  }, []);

  useEffect(() => {
    // MA-PHASE11A-P0-AUDIT-005: Clear items immediately when account switches
    // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
    if (!selectedTenantId) return;
    setItems([]);
    setTotal(0);
    fetchCampaigns();
  }, [fetchCampaigns, selectedTenantId]);

  useEffect(() => {
    // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
    if (!selectedTenantId) return;
    fetchSummary();
  }, [fetchSummary, selectedTenantId]);

  useEffect(() => {
    setPage(1);
  }, [qDebounced, filtersApplied]);

  // ====== FILTER HANDLERS ======

  const applyFilters = () => {
    setFiltersApplied(filtersDraft);
  };

  const clearFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
  };

  // ====== ACTIONS ======

  const handleCreate = () => {
    setEditingCampaign(null);
    setModalOpen(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingCampaign) {
        await api.put(`/marketing-campaigns/${editingCampaign.id}`, formData);
        toast({ title: "Campaign updated" });
      } else {
        await api.post("/marketing-campaigns", formData);
        toast({ title: "Campaign created" });
      }
      fetchCampaigns();
      fetchSummary();
    } catch (e) {
      toast({
        title: editingCampaign ? "Update failed" : "Create failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
      throw e;
    }
  };

  const handleStatusChange = async (campaign, newStatus) => {
    try {
      await api.patch(`/marketing-campaigns/${campaign.id}/status`, { status: newStatus });
      toast({ title: `Campaign ${newStatus}` });
      fetchCampaigns();
      fetchSummary();
    } catch (e) {
      toast({ title: "Status change failed", variant: "destructive" });
    }
  };

  const handleDelete = (campaign) => {
    setDeleteConfirm({ open: true, campaign });
  };

  const handleCopyTrackingUrl = async (campaign) => {
    const trackingParams = generateTrackingParams(campaign);
    try {
      await navigator.clipboard.writeText(`?${trackingParams}`);
      toast({ 
        title: "Tracking params copied",
        description: "Append these params to your landing page URL.",
      });
    } catch (e) {
      toast({ 
        title: "Copy manually", 
        description: `Append to your landing page: ?${trackingParams}`,
        duration: 10000,
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.campaign) return;
    try {
      await api.delete(`/marketing-campaigns/${deleteConfirm.campaign.id}`);
      toast({ title: "Campaign deleted" });
      fetchCampaigns();
      fetchSummary();
    } catch (e) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  // ====== SORTING ======

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
  };

  const clearSort = () => {
    setSortBy("");
    setSortDir("asc");
  };

  // Sort items client-side for now
  const sortedItems = useMemo(() => {
    if (!sortBy) return items;
    return [...items].sort((a, b) => {
      let aVal = a[sortBy] ?? a.metrics?.[sortBy] ?? "";
      let bVal = b[sortBy] ?? b.metrics?.[sortBy] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (sortDir === "asc") return aVal.localeCompare(bVal);
      return bVal.localeCompare(aVal);
    });
  }, [items, sortBy, sortDir]);

  // ====== SELECTION ======

  const allOnPageSelected = items.length > 0 && items.every((x) => selected.has(x.id));

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        items.forEach((x) => next.add(x.id));
      } else {
        items.forEach((x) => next.delete(x.id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const anySelected = selected.size > 0;

  const deleteSelected = async () => {
    try {
      const selectedItems = items.filter((x) => selected.has(x.id));
      await Promise.all(selectedItems.map((c) => api.delete(`/marketing-campaigns/${c.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      fetchCampaigns();
      fetchSummary();
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  // ====== PAGINATION ======

  const totalPages = Math.ceil(total / pageSize);

  // ====== RENDER CELL VALUE ======

  const renderCellValue = (campaign, key) => {
    if (key === "status") {
      return <CampaignStatusBadge status={campaign.status} />;
    }
    if (key === "source_platform") {
      return SOURCE_PLATFORM_OPTIONS.find(p => p.value === campaign.source_platform)?.label || campaign.source_platform;
    }
    if (key === "lead_count") {
      return campaign.metrics?.lead_count ?? 0;
    }
    if (key === "qualified_count") {
      return campaign.metrics?.qualified_count ?? 0;
    }
    if (key === "booked_count") {
      return campaign.metrics?.booked_count ?? 0;
    }
    return campaign[key] || "—";
  };

  // ====== RENDER ======

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="campaigns-page">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5 shrink-0">
        <SummaryCard icon={Megaphone} label="Total Campaigns" value={summary.total_campaigns} color="indigo" />
        <SummaryCard icon={Play} label="Active" value={summary.active_campaigns} color="green" />
        <SummaryCard icon={Users} label="Total Leads" value={summary.total_leads} color="cyan" />
        <SummaryCard icon={UserCheck} label="Qualified" value={summary.total_qualified} color="amber" />
        <SummaryCard icon={CalendarCheck} label="Booked" value={summary.total_booked} color="green" />
      </div>

      {/* Controls Row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          {/* View Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="campaigns-view-dropdown"
              >
                All Campaigns
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setFiltersApplied({});
                  setFiltersDraft({});
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                All Campaigns
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFiltersApplied({ status: { operator: "is", value: "active" } });
                  setFiltersDraft({ status: { operator: "is", value: "active" } });
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Active Campaigns
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFiltersApplied({ status: { operator: "is", value: "paused" } });
                  setFiltersDraft({ status: { operator: "is", value: "paused" } });
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Paused Campaigns
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setFiltersApplied({ status: { operator: "is", value: "draft" } });
                  setFiltersDraft({ status: { operator: "is", value: "draft" } });
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Draft Campaigns
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Bulk Actions */}
          {anySelected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="campaigns-bulk-actions">
                  Actions ({selected.size})
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem
                  onClick={deleteSelected}
                  className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2">
          <CollapsibleSearch
            value={q}
            onChange={setQ}
            placeholder="Search campaigns..."
            width="w-[240px]"
            data-testid="campaigns-search"
          />

          {/* Filter Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-label="Open filters"
            data-testid="campaigns-filter-btn"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : ""}`} />
            {filtersActive && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" />
            )}
          </Button>

          {/* Manage Columns Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setColumnsOpen(true)}
            aria-label="Manage columns"
            data-testid="campaigns-manage-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          {/* MA-PHASE10: Hide Create button if user lacks campaigns module */}
          {canManageCampaigns && (
            <Button onClick={handleCreate} data-testid="create-campaign-btn">
              <Plus className="mr-2 h-4 w-4" /> New Campaign
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (filtersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {/* Filter Panel */}
        {filtersOpen && (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="campaigns-filters-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                title="Filter Campaigns by"
                fields={FILTER_FIELD_DEFS}
                optionsByField={filterOptions}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={applyFilters}
                onClear={clearFilters}
              />
            </CardContent>
          </Card>
        )}

        {/* Campaigns Table Card */}
        <Card className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="campaigns-table-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading...
              </div>
            ) : (
              <div className="flex-1 min-w-0 overflow-auto">
                <Table>
                  <TableHeader className="relative after:pointer-events-none after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:-translate-y-3 after:bg-white/10 after:content-['']">
                    <TableRow className="border-transparent hover:bg-transparent">
                      <TableHead colSpan={tableColumns.length + 2} className="h-auto p-1 text-slate-200/70">
                        <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={allOnPageSelected}
                              onCheckedChange={toggleAllOnPage}
                              className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              data-testid="campaigns-select-all"
                            />
                          </div>

                          <div className="min-w-0 grid items-center gap-3 pr-2" style={{ gridTemplateColumns: tableColumns.map(() => 'minmax(100px, 1fr)').join(' ') }}>
                            {tableColumns.map((key) => (
                              <div key={key} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                                <span className="min-w-0 flex-1 truncate">{prettifyFieldLabel(key)}</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={
                                        "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                        (sortBy === key ? " bg-white/10" : "")
                                      }
                                      aria-label={`Sort ${prettifyFieldLabel(key)}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem
                                      onClick={() => applySort(key, "asc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => applySort(key, "desc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={clearSort}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                    >
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>

                          <div className="h-9 w-9" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedItems.length === 0 ? (
                      <TableRow className="border-transparent">
                        <TableCell colSpan={tableColumns.length + 2} className="py-16 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <Megaphone className="h-12 w-12 text-indigo-400 mb-3" />
                            <div className="text-sm text-slate-200/70">
                              {q || filtersActive 
                                ? "No campaigns match your filters." 
                                : "No campaigns yet. Create your first campaign to start tracking traffic sources."}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedItems.map((campaign) => (
                        <TableRow key={campaign.id} className="border-transparent">
                          <TableCell colSpan={tableColumns.length + 2} className="p-1 align-top">
                            <div className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10">
                              <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selected.has(campaign.id)}
                                    onCheckedChange={() => toggleOne(campaign.id)}
                                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                  />
                                </div>
                                <div className="min-w-0 grid items-center gap-3 pr-2" style={{ gridTemplateColumns: tableColumns.map(() => 'minmax(100px, 1fr)').join(' ') }}>
                                  {tableColumns.map((key) => (
                                    <div key={key} className="min-w-0">
                                      {key === "name" ? (
                                        <div>
                                          <div className="truncate font-medium text-slate-100">{campaign.name}</div>
                                          {campaign.utm_campaign && (
                                            <div className="text-xs text-slate-200/50">utm: {campaign.utm_campaign}</div>
                                          )}
                                        </div>
                                      ) : key === "status" ? (
                                        <CampaignStatusBadge status={campaign.status} />
                                      ) : (
                                        <div className="truncate text-slate-100/90">{renderCellValue(campaign, key)}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex items-center justify-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-9 w-9">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                      <DropdownMenuItem onClick={() => handleCopyTrackingUrl(campaign)}>
                                        <Link className="mr-2 h-4 w-4" /> Copy Tracking URL
                                      </DropdownMenuItem>
                                      {/* MA-PHASE10: Hide Edit/Delete/Status actions if user lacks campaigns module */}
                                      {canManageCampaigns && (
                                        <>
                                          <DropdownMenuItem onClick={() => handleEdit(campaign)}>
                                            <Edit className="mr-2 h-4 w-4" /> Edit
                                          </DropdownMenuItem>
                                          {campaign.status !== "active" && (
                                            <DropdownMenuItem onClick={() => handleStatusChange(campaign, "active")}>
                                              <Play className="mr-2 h-4 w-4" /> Activate
                                            </DropdownMenuItem>
                                          )}
                                          {campaign.status === "active" && (
                                            <DropdownMenuItem onClick={() => handleStatusChange(campaign, "paused")}>
                                              <Pause className="mr-2 h-4 w-4" /> Pause
                                            </DropdownMenuItem>
                                          )}
                                          {campaign.status !== "archived" && (
                                            <DropdownMenuItem onClick={() => handleStatusChange(campaign, "archived")}>
                                              <Archive className="mr-2 h-4 w-4" /> Archive
                                            </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem
                                            onClick={() => handleDelete(campaign)}
                                            className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Footer - Always visible */}
            <div data-testid="campaigns-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70">
                {anySelected ? `Selected: ${selected.size}` : `Total Campaigns: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {page > 1 && (
                  <Button
                    variant="secondary"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                )}

                {page < totalPages && (
                  <Button
                    variant="secondary"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                )}

                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <CampaignModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaign={editingCampaign}
        onSave={handleSave}
      />

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm((prev) => ({ ...prev, open }))}
        campaign={deleteConfirm.campaign}
        onConfirm={confirmDelete}
      />

      <CampaignColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        columns={tableColumns}
        onSave={setTableColumns}
      />
    </div>
  );
}
